<script setup lang="ts">
import striptags from 'striptags'
import type { Component } from 'vue'
import type { RouterQueryParams } from '../../types/router-query'
import { getHostname } from '@/api'
import { ElementalTypeEnum, getRouteFromElemental } from '~~/src/types/elemental'
import { SEO } from '@/utils/constants'
import { getBreadcrumbsJsonLd, getProductJsonLdFromElemental } from '~~/src/helpers/serp'
import { useGetElementalQuery } from '~~/src/composables/query/elementals/useGetElementalQuery'
import { useOwnershipDataQuery } from '~~/src/composables/query/elementals/useOwnershipDataQuery'
import { capitalizeFirstLetter } from '@/utils/formatting'
import ElementProvider from '@/components/Elemental/context/ElementProvider.vue'

const { type, forceView } = defineProps<
{ type: ElementalTypeEnum; forceView?: RouterQueryParams['view_type'] }
>()

const Views: Record<RouterQueryParams['view_type'], Component> = {
  feed: defineAsyncComponent(() => import('@/components/View/Feed/index.vue')),
  public: defineAsyncComponent(() => import('@/components/Product/Container/index.vue')),
  board: defineAsyncComponent(() => import('@/components/View/Board/index.vue')),

}

const route = useRoute()
const id = computed(() => asArray(route.params.id)[0])

if (!id.value)
  throw showError({ statusCode: 404, message: 'Not found', fatal: true })

const user = useUser()
const routePath = ref(getRouteFromElemental(route.path))
const { data: elemental, suspense, refetch, error, isLoading } = useGetElementalQuery({
  id,
  typeRoute: type,
})

try {
  await suspense()
  if (error.value)
    throw showError({ statusCode: 404, message: 'Not found', fatal: true })

  if (elemental) {
    const tipQuery = route.query.paid_tip
    if (elemental.value?.url && id.value !== elemental.value.url)
      navigateTo(`/${route.path}/${elemental.value.url}${tipQuery ? `?paid_tip=${tipQuery}` : ''}`)
  }
}
catch (e) {
  // eslint-disable-next-line no-console
  console.log('Page not found or it is private')
}

function getViewType() {
  const view = (route.query?.view_type as RouterQueryParams['view_type'] ?? 'public')
  const isBoard = elemental.value?.contentTypeId === ElementalTypeEnum.Image
  if (forceView)
    return forceView
  if (isBoard && view === 'public')
    return 'board'
  return view
}

const viewType = computed(() => getViewType())

const { cover = [], output = [] } = elemental?.value?.images ?? {}
const featuredImage = cover.at(-1)?.url ?? output.at(0)?.url ?? SEO.image

const optimizedSocialImage = optimizeImage(featuredImage, {
  width: 1200,
  height: 630,
  crop: 'at_max',
  focus: 'auto',
  quality: 80,
  format: 'webp',
})

const host = getHostname()
const elementalUrl = `${host}/${routePath.value}/${elemental.value?.url || elemental.value?.id}`

let description = ''
if (elemental.value?.description)
  description = striptags(elemental.value?.description)

if (elemental.value) {
  const breadcrumbs = [
    { name: elemental.value?.title, url: elementalUrl },
  ]
  const jsonldBreadcrumbs = getBreadcrumbsJsonLd(breadcrumbs)

  const jsonldProduct = getProductJsonLdFromElemental({
    elemental: elemental.value,
    elementalUrl,
    description,
    featuredImage,
    category: capitalizeFirstLetter(getElementalTypeRoute(type)),
  })

  useHead({
    link: [{ rel: 'canonical', href: elementalUrl }],
    meta: [
      { name: 'og:title', content: elemental.value?.title },
      { name: 'og:description', content: description },
      { name: 'og:url', content: elementalUrl },
      { name: 'og:image', content: optimizedSocialImage },
      { name: 'twitter:title', content: elemental.value?.title.substring(0, 70) },
      { name: 'twitter:description', content: description.substring(0, 200) },
      { name: 'twitter:image', content: optimizedSocialImage },
      { name: 'twitter:site', content: elementalUrl },
    ],
    script: [
      {
        hid: 'page-json-ld',
        type: 'application/ld+json',
        innerHTML: JSON.stringify([jsonldBreadcrumbs, jsonldProduct]),
      },
    ],
  })

  useSeoMeta({
    title: `${elemental.value?.title} | Snack Prompt`.substring(0, 75),
    description: `${description}`.substring(0, 170),
    ogTitle: elemental.value?.title,
    ogDescription: description,
    ogUrl: elemental.value?.url,
    ogImage: optimizedSocialImage,
    twitterSite: elementalUrl,
    twitterCreator: SEO.twitter,
    twitterTitle: elemental.value?.title,
    twitterDescription: description,
    twitterImage: optimizedSocialImage,
  })
}

const haveUser = computed(() => !!user.value)
const isAdmin = computed(() => user.value?.isAdmin)
const { data: ownershipData, refetch: refreshOwnershipData } = useOwnershipDataQuery({
  id: elemental.value?.id ?? id,
  isAdmin,
}, {
  enabled: haveUser,
})

watch(user, () => {
  if (user.value) {
    refetch()
    refreshOwnershipData()
  }
})

const isOwner = computed(() => elemental.value?.user.id === user.value?.id)
const hiddeHeaderOnMobile = viewType !== 'feed'
</script>

<template>
  <ElementProvider
    :elemental="elemental"
    :type="type"
    :ownership="ownershipData"
    :is-loading="isLoading"
    :is-owner="isOwner"
  >
    <NuxtLayout>
      <template #body-content>
        <component :is="Views[viewType]" />
      </template>
    </NuxtLayout>
  </ElementProvider>
</template>
